import { AUDIO_DEVICE_FAILURES } from './audioDevices'

export const AUDIO_DEVICE_KINDS = Object.freeze({
  INPUT: 'input',
  OUTPUT: 'output'
})

export const SYSTEM_DEFAULT_LABEL = 'System default'

const NOUNS = {
  [AUDIO_DEVICE_KINDS.INPUT]: 'Microphone',
  [AUDIO_DEVICE_KINDS.OUTPUT]: 'Speaker'
}

function _name(label) {
  return label || SYSTEM_DEFAULT_LABEL
}

/**
 * What the agent is told after picking a device, as `{ level, message }` with `level` one of
 * `success`, `warning` and `error`.
 *
 * Every change gets an answer. A device change that fails silently looks to the agent exactly like
 * the faults this control exists to rule out - "I cannot hear the caller", "the caller cannot hear
 * me" - so a failure names the device that is STILL in use, and a success names the one that now
 * is, so the agent never has to guess which one the call is on.
 */
export function describeAudioDeviceSelection({ kind, label = '', previousLabel = '', result }) {
  const noun = NOUNS[kind] || 'Device'
  const chosen = _name(label)
  const previous = _name(previousLabel)

  if (result?.ok) {
    if (result.isConstraintFloorMissing) {
      return {
        level: 'warning',
        message: `${noun} switched to ${chosen}, but echo cancellation could not be turned on. The caller may hear an echo.`
      }
    }

    if (result.isDeferred) {
      return {
        level: 'success',
        message: `${noun} set to ${chosen}. It will be used when you go live.`
      }
    }

    return { level: 'success', message: `${noun} switched to ${chosen}` }
  }

  switch (result?.reason) {
  case AUDIO_DEVICE_FAILURES.UNSUPPORTED:
    return {
      level: 'error',
      message: 'This browser cannot choose a speaker. Sound plays through your system speaker.'
    }
  case AUDIO_DEVICE_FAILURES.NOT_FOUND:
    return {
      level: 'error',
      message: `${chosen} is not available. Still using ${previous}.`
    }
  case AUDIO_DEVICE_FAILURES.OTHER_TAB:
    return {
      level: 'error',
      message: 'Change your devices in the tab that is taking calls.'
    }
  default:
    return {
      level: 'error',
      message: kind === AUDIO_DEVICE_KINDS.INPUT
        ? `Could not switch the microphone to ${chosen}. It may be in use by another app. Still using ${previous}.`
        : `Could not switch the speaker to ${chosen}. Still using ${previous}.`
    }
  }
}
